import React from 'react';
import { UnifiedCalendarItem, CALENDAR_ITEM_COLORS, toLocalDateString } from '../../../types/calendar';
import { useAndreiChat } from '../../../contexts/AndreiChatContext';

/**
 * One calendar item AS IT APPEARS ON ONE DAY.
 *
 * A three-day install is a single `UnifiedCalendarItem` but occupies three day
 * cells, so the grid can no longer hand consumers a bare item — it has to say
 * WHICH day of the span this is. Without that, day two and day three of an
 * install render as "starts today, 8:00 AM", which is exactly the double-booking
 * misread this wave exists to remove.
 *
 * Defined here rather than in FirebaseCalendarComponent so the import stays
 * one-way (the grid already imports this module; the reverse would be a cycle).
 */
export interface DayItemEntry {
  item: UnifiedCalendarItem;
  /** True on the item's FIRST day. False on a continuation day. */
  isStart: boolean;
  /** True on the item's LAST day. Also true for single-day items. */
  isEnd: boolean;
  /** 1-based position of this day within the span. */
  dayIndex: number;
  /** Total days the item spans. 1 for a single-day item. */
  totalDays: number;
}

interface DayDetailPanelProps {
  isOpen: boolean;
  selectedDate: Date | null;
  items: DayItemEntry[];
  onClose: () => void;
  onItemClick: (item: UnifiedCalendarItem) => void;
  /** Receives the day the panel is showing, so a new event lands on THAT date and not today. */
  onAddEvent: (date?: Date) => void;
}

/**
 * A `<button>` carries the click affordance these rows always had, but the UA
 * stylesheet would also drag in Arial, centred text and shrink-to-fit width.
 * Reset only those — background, border, padding and radius stay with the class
 * so the rendered card is byte-for-byte what it was as a `<div>`.
 */
const ROW_BUTTON_RESET: React.CSSProperties = {
  appearance: 'none',
  width: '100%',
  textAlign: 'left',
  fontFamily: 'inherit',
  lineHeight: 'inherit',
  color: 'inherit',
};

const DayDetailPanel: React.FC<DayDetailPanelProps> = ({
  isOpen,
  selectedDate,
  items,
  onClose,
  onItemClick,
  onAddEvent,
}) => {
  // MUST be called before the early return below — React requires the same hook
  // order on every render, and `selectedDate` is null whenever the panel is shut.
  const { openAndreiChat } = useAndreiChat();
  const askAndreiAboutDay = React.useCallback(() => {
    if (!selectedDate) return;
    const day = toLocalDateString(selectedDate);
    const label = selectedDate.toLocaleDateString('en-AU', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    });
    openAndreiChat({
      context: { type: 'calendar', id: day, date: day, label },
      prefill: `What's on for ${label} (${day})? Anything I should prepare or follow up?`,
    });
  }, [openAndreiChat, selectedDate]);

  if (!selectedDate) return null;

  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const formatCurrency = (amount: number, currency: string = 'AUD', digits = 0) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency,
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(amount);
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-AU', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'event': return 'bi-calendar-event';
      case 'invoice_due': return 'bi-receipt';
      case 'invoice_created': return 'bi-file-earmark-plus';
      case 'recurring_invoice':
      case 'recurring_expense': return 'bi-arrow-repeat';
      case 'bill_due': return 'bi-receipt';
      case 'job_due': return 'bi-briefcase';
      default: return 'bi-calendar';
    }
  };

  const getIconClass = (type: string) => {
    switch (type) {
      case 'event': return 'modern-calendar__panel-event-icon--event';
      case 'invoice_due': return 'modern-calendar__panel-event-icon--invoice';
      case 'invoice_created': return 'modern-calendar__panel-event-icon--created';
      case 'recurring_invoice': return 'modern-calendar__panel-event-icon--recurring';
      case 'recurring_expense': return 'modern-calendar__panel-event-icon--expense';
      case 'bill_due': return 'modern-calendar__panel-event-icon--bill';
      // 'job_due' deliberately has no class — the stylesheet has no --job tile and
      // the icon glyph is white, so it would render invisible. It is coloured
      // inline from the item's own colour instead (see iconStyle below).
      default: return '';
    }
  };

  const getAmountClass = (type: string) => {
    switch (type) {
      case 'invoice_due': return 'modern-calendar__panel-event-amount--invoice';
      case 'invoice_created': return 'modern-calendar__panel-event-amount--created';
      case 'recurring_invoice': return 'modern-calendar__panel-event-amount--recurring';
      case 'recurring_expense': return 'modern-calendar__panel-event-amount--expense';
      case 'bill_due': return 'modern-calendar__panel-event-amount--bill';
      default: return '';
    }
  };

  const isTemplateType = (type: string) =>
    type === 'recurring_invoice' || type === 'recurring_expense';

  /** Types whose tile colour comes from the item, not from a stylesheet class. */
  const iconStyle = (item: UnifiedCalendarItem): React.CSSProperties | undefined => {
    if (item.type === 'event') return { background: item.color || CALENDAR_ITEM_COLORS.event };
    if (item.type === 'job_due') return { background: item.color || CALENDAR_ITEM_COLORS.job_due };
    return undefined;
  };

  /**
   * The meta line for a multi-day item. On a continuation day the item's own
   * start TIME is a lie ("8:00 AM" on day two of a three-day install), so it is
   * replaced with "Ongoing" and the position within the span is always spelled out.
   */
  const spanMeta = (entry: DayItemEntry, leading: string | null): string => {
    const parts: string[] = [];
    if (!entry.isStart) {
      parts.push('Ongoing');
    } else if (leading) {
      parts.push(leading);
    }
    if (entry.totalDays > 1) {
      parts.push(`Day ${entry.dayIndex} of ${entry.totalDays}`);
    }
    return parts.join(' • ');
  };

  // Group items by type. Everything reads `entry.item`, so a continuation day
  // lands in exactly the same section as its first day.
  const events = items.filter(e => e.item.type === 'event');
  const invoices = items.filter(e => e.item.type === 'invoice_due');
  const created = items.filter(e => e.item.type === 'invoice_created');
  const recurring = items.filter(e => e.item.type === 'recurring_invoice');
  const expenses = items.filter(e => e.item.type === 'recurring_expense');
  const bills = items.filter(e => e.item.type === 'bill_due');
  const jobs = items.filter(e => e.item.type === 'job_due');

  const isToday = selectedDate.toDateString() === new Date().toDateString();

  return (
    <div className={`modern-calendar__detail-panel ${isOpen ? 'modern-calendar__detail-panel--open' : ''}`}>
      {/* Header */}
      <div className="modern-calendar__panel-header">
        <div className="modern-calendar__panel-date">
          <div className="modern-calendar__panel-day">{selectedDate.getDate()}</div>
          <div className="modern-calendar__panel-weekday">
            {dayNames[selectedDate.getDay()]}, {monthNames[selectedDate.getMonth()]}
            {isToday && <span className="badge badge-primary ms-2">Today</span>}
          </div>
        </div>
        <button className="modern-calendar__panel-close" onClick={onClose}>
          <i className="bi bi-x-lg"></i>
        </button>
      </div>

      {/* Content */}
      <div className="modern-calendar__panel-content">
        {items.length === 0 ? (
          <div className="modern-calendar__panel-empty">
            <i className="bi bi-calendar-x d-block"></i>
            <p>No events scheduled</p>
          </div>
        ) : (
          <>
            {/* Events Section */}
            {events.length > 0 && (
              <div className="modern-calendar__panel-section">
                <div className="modern-calendar__panel-section-title">
                  <span
                    className="section-dot"
                    style={{ background: events[0]?.item.color || CALENDAR_ITEM_COLORS.event }}
                  ></span>
                  Events ({events.length})
                </div>
                {events.map(entry => (
                  <button
                    type="button"
                    key={`${entry.item.id}-${entry.dayIndex}`}
                    className="modern-calendar__panel-event"
                    style={ROW_BUTTON_RESET}
                    onClick={() => onItemClick(entry.item)}
                  >
                    <div
                      className={`modern-calendar__panel-event-icon ${getIconClass(entry.item.type)}`}
                      style={iconStyle(entry.item)}
                    >
                      <i className={`bi ${(entry.item.icon || getIcon(entry.item.type))}`}></i>
                    </div>
                    <div className="modern-calendar__panel-event-content">
                      <div className="modern-calendar__panel-event-title">
                        {entry.item.eventData?.title || entry.item.title}
                      </div>
                      <div className="modern-calendar__panel-event-meta">
                        {spanMeta(entry, entry.item.allDay ? 'All day' : formatTime(entry.item.start))}
                        {entry.item.eventData?.location && ` • ${entry.item.eventData.location}`}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Jobs Section — open jobs on their due date (same source and open-test
                as the Today timeline, so the two surfaces cannot disagree). */}
            {jobs.length > 0 && (
              <div className="modern-calendar__panel-section">
                <div className="modern-calendar__panel-section-title">
                  <span className="section-dot" style={{ background: CALENDAR_ITEM_COLORS.job_due }}></span>
                  Jobs and support ({jobs.length})
                </div>
                {jobs.map(entry => (
                  <button
                    type="button"
                    key={`${entry.item.id}-${entry.dayIndex}`}
                    className="modern-calendar__panel-event"
                    style={ROW_BUTTON_RESET}
                    onClick={() => onItemClick(entry.item)}
                  >
                    <div
                      className={`modern-calendar__panel-event-icon ${getIconClass(entry.item.type)}`}
                      style={iconStyle(entry.item)}
                    >
                      <i className={`bi ${(entry.item.icon || getIcon(entry.item.type))}`}></i>
                    </div>
                    <div className="modern-calendar__panel-event-content">
                      <div className="modern-calendar__panel-event-title">
                        {entry.item.jobData?.jobLabel || entry.item.title}
                      </div>
                      <div className="modern-calendar__panel-event-meta">
                        {entry.item.commitment ? entry.item.description : entry.item.jobData?.status || 'Job due'}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Invoices Due Section */}
            {invoices.length > 0 && (
              <div className="modern-calendar__panel-section">
                <div className="modern-calendar__panel-section-title">
                  <span className="section-dot" style={{ background: CALENDAR_ITEM_COLORS.invoice_due }}></span>
                  Invoices Due ({invoices.length})
                </div>
                {invoices.map(entry => (
                  <button
                    type="button"
                    key={`${entry.item.id}-${entry.dayIndex}`}
                    className="modern-calendar__panel-event"
                    style={ROW_BUTTON_RESET}
                    onClick={() => onItemClick(entry.item)}
                  >
                    <div className={`modern-calendar__panel-event-icon ${getIconClass(entry.item.type)}`}>
                      <i className={`bi ${(entry.item.icon || getIcon(entry.item.type))}`}></i>
                    </div>
                    <div className="modern-calendar__panel-event-content">
                      <div className="modern-calendar__panel-event-title">
                        {entry.item.invoiceData?.InvoiceNumber || 'Invoice'}
                      </div>
                      <div className="modern-calendar__panel-event-meta">
                        {entry.item.customerName || 'Unknown customer'}
                      </div>
                    </div>
                    {entry.item.amount !== undefined && (
                      <div className={`modern-calendar__panel-event-amount ${getAmountClass(entry.item.type)}`}>
                        {formatCurrency(entry.item.amount, entry.item.currencyCode, entry.item.commitment?.kind === 'stripe' ? 2 : 0)}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Invoices Created Section */}
            {created.length > 0 && (
              <div className="modern-calendar__panel-section">
                <div className="modern-calendar__panel-section-title">
                  <span className="section-dot" style={{ background: CALENDAR_ITEM_COLORS.invoice_created }}></span>
                  Invoices Created ({created.length})
                </div>
                {created.map(entry => (
                  <button
                    type="button"
                    key={`${entry.item.id}-${entry.dayIndex}`}
                    className="modern-calendar__panel-event"
                    style={ROW_BUTTON_RESET}
                    onClick={() => onItemClick(entry.item)}
                  >
                    <div className={`modern-calendar__panel-event-icon ${getIconClass(entry.item.type)}`}>
                      <i className={`bi ${(entry.item.icon || getIcon(entry.item.type))}`}></i>
                    </div>
                    <div className="modern-calendar__panel-event-content">
                      <div className="modern-calendar__panel-event-title">
                        {entry.item.invoiceData?.InvoiceNumber || 'Invoice'}
                      </div>
                      <div className="modern-calendar__panel-event-meta">
                        {entry.item.customerName || 'Unknown customer'}
                      </div>
                    </div>
                    {entry.item.amount !== undefined && (
                      <div className={`modern-calendar__panel-event-amount ${getAmountClass(entry.item.type)}`}>
                        {formatCurrency(entry.item.amount, entry.item.currencyCode, entry.item.commitment?.kind === 'stripe' ? 2 : 0)}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Scheduled Invoices Section */}
            {recurring.length > 0 && (
              <div className="modern-calendar__panel-section">
                <div className="modern-calendar__panel-section-title">
                  <span className="section-dot" style={{ background: CALENDAR_ITEM_COLORS.recurring_invoice }}></span>
                  {recurring.some(entry => entry.item.commitment?.kind === 'stripe') ? 'Upcoming billing' : 'Scheduled Invoices'} ({recurring.length})
                </div>
                {recurring.map(entry => (
                  <button
                    type="button"
                    key={`${entry.item.id}-${entry.dayIndex}`}
                    className={`modern-calendar__panel-event${isTemplateType(entry.item.type) ? ' modern-calendar__panel-event--template' : ''}`}
                    style={ROW_BUTTON_RESET}
                    onClick={() => onItemClick(entry.item)}
                  >
                    <div className={`modern-calendar__panel-event-icon ${getIconClass(entry.item.type)}`}>
                      <i className={`bi ${(entry.item.icon || getIcon(entry.item.type))}`}></i>
                    </div>
                    <div className="modern-calendar__panel-event-content">
                      <div className="modern-calendar__panel-event-title">
                        {entry.item.customerName || 'Recurring Invoice'}
                      </div>
                      <div className="modern-calendar__panel-event-meta">
                        {entry.item.commitment?.kind === 'stripe' ? `Stripe · ${entry.item.commitment.estimated ? 'estimated date' : 'projected payment'}` : entry.item.repeatingInvoiceData?.reference || 'Scheduled'}
                      </div>
                    </div>
                    {entry.item.amount !== undefined && (
                      <div className={`modern-calendar__panel-event-amount ${getAmountClass(entry.item.type)}`}>
                        {formatCurrency(entry.item.amount, entry.item.currencyCode, entry.item.commitment?.kind === 'stripe' ? 2 : 0)}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Scheduled Bills Section */}
            {expenses.length > 0 && (
              <div className="modern-calendar__panel-section">
                <div className="modern-calendar__panel-section-title">
                  <span className="section-dot" style={{ background: CALENDAR_ITEM_COLORS.recurring_expense }}></span>
                  Scheduled Bills ({expenses.length})
                </div>
                {expenses.map(entry => (
                  <button
                    type="button"
                    key={`${entry.item.id}-${entry.dayIndex}`}
                    className={`modern-calendar__panel-event${isTemplateType(entry.item.type) ? ' modern-calendar__panel-event--template' : ''}`}
                    style={ROW_BUTTON_RESET}
                    onClick={() => onItemClick(entry.item)}
                  >
                    <div className={`modern-calendar__panel-event-icon ${getIconClass(entry.item.type)}`}>
                      <i className={`bi ${(entry.item.icon || getIcon(entry.item.type))}`}></i>
                    </div>
                    <div className="modern-calendar__panel-event-content">
                      <div className="modern-calendar__panel-event-title">
                        {entry.item.customerName || 'Recurring Expense'}
                      </div>
                      <div className="modern-calendar__panel-event-meta">
                        {entry.item.repeatingInvoiceData?.reference || 'Scheduled'}
                      </div>
                    </div>
                    {entry.item.amount !== undefined && (
                      <div className={`modern-calendar__panel-event-amount ${getAmountClass(entry.item.type)}`}>
                        {formatCurrency(entry.item.amount, entry.item.currencyCode, entry.item.commitment?.kind === 'stripe' ? 2 : 0)}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Bills Section */}
            {bills.length > 0 && (
              <div className="modern-calendar__panel-section">
                <div className="modern-calendar__panel-section-title">
                  <span className="section-dot" style={{ background: CALENDAR_ITEM_COLORS.bill_due }}></span>
                  Bills ({bills.length})
                </div>
                {bills.map(entry => (
                  <button
                    type="button"
                    key={`${entry.item.id}-${entry.dayIndex}`}
                    className="modern-calendar__panel-event"
                    style={ROW_BUTTON_RESET}
                    onClick={() => onItemClick(entry.item)}
                  >
                    <div className={`modern-calendar__panel-event-icon ${getIconClass(entry.item.type)}`}>
                      <i className={`bi ${(entry.item.icon || getIcon(entry.item.type))}`}></i>
                    </div>
                    <div className="modern-calendar__panel-event-content">
                      <div className="modern-calendar__panel-event-title">
                        {entry.item.invoiceData?.InvoiceNumber || 'Bill'}
                      </div>
                      <div className="modern-calendar__panel-event-meta">
                        {entry.item.customerName || 'Unknown supplier'}
                      </div>
                    </div>
                    {entry.item.amount !== undefined && (
                      <div className={`modern-calendar__panel-event-amount ${getAmountClass(entry.item.type)}`}>
                        {formatCurrency(entry.item.amount, entry.item.currencyCode, entry.item.commitment?.kind === 'stripe' ? 2 : 0)}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Actions */}
      <div className="modern-calendar__panel-actions">
        <button className="modern-calendar__panel-add-btn" onClick={() => onAddEvent(selectedDate ?? undefined)}>
          <i className="bi bi-plus-lg"></i>
          Add Event
        </button>
        {/* The day-level Andrei door. The date travels as a proper `calendar`
            context handle AND is restated in the prefill, because the message
            text is what actually reaches the model today. */}
        <button
          className="modern-calendar__panel-add-btn"
          onClick={askAndreiAboutDay}
          disabled={!selectedDate}
          title="Ask Andrei about this day"
        >
          <i className="bi bi-stars"></i>
          Ask Andrei
        </button>
      </div>
    </div>
  );
};

export default DayDetailPanel;
